import React, { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw, X, Copy, Check } from 'lucide-react';
import { isNative } from '../lib/native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AUTH_ERROR_MESSAGES = {
  google_access_denied: "Google sign-in was cancelled.",
  invalid_state: "Your sign-in session expired. Please try again.",
  token_exchange_failed: "Couldn't complete Google sign-in. Please try again.",
  invalid_id_token: "Couldn't verify Google sign-in. Please try again.",
  missing_email: "Your Google account didn't provide an email address.",
  session_failed: "Signed in with Google, but couldn't start a session. Please try again.",
  missing_code: "Google sign-in didn't complete. Please try again.",
};

const FALLBACK_MESSAGE = "Sign-in failed. Please try again.";

const CONFIG_ERRORS = new Set(["google_not_configured", "oauth_not_configured"]);

function NotConfiguredContent() {
  const [copied, setCopied] = useState(false);
  const redirectUri = `${window.location.origin}/api/auth/google/oauth/callback`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(redirectUri);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select text for manual copy
      const el = document.querySelector('[data-testid="oauth-redirect-uri"]');
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
      }
    }
  };

  return (
    <div className="auth-error-config" data-testid="auth-error-config">
      <p className="auth-error-message">
        Google sign-in isn't configured for this environment yet.
      </p>
      <div className="auth-error-uri-row">
        <span className="auth-error-uri-label">Redirect URI for Google Console:</span>
        <div className="auth-error-uri-wrap">
          <code className="auth-error-uri" data-testid="oauth-redirect-uri">{redirectUri}</code>
          <button
            className="auth-error-copy"
            onClick={handleCopy}
            title={copied ? "Copied!" : "Copy redirect URI"}
            data-testid="copy-redirect-uri-btn"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
      </div>
      <p className="auth-error-hint">
        Set <code>GOOGLE_OAUTH_CLIENT_ID</code> and <code>GOOGLE_OAUTH_CLIENT_SECRET</code> in backend env.
      </p>
    </div>
  );
}

export function useAuthError() {
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const error = url.searchParams.get('auth_error');

    if (error) {
      setAuthError(error);
      // Clean auth_error from URL without reload
      url.searchParams.delete('auth_error');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  return [authError, setAuthError];
}

export function AuthErrorBanner({ variant = 'inline' }) {
  const [authError, setAuthError] = useAuthError();

  if (!authError) return null;

  const isConfigError = CONFIG_ERRORS.has(authError);
  const message = isConfigError ? null : (AUTH_ERROR_MESSAGES[authError] || FALLBACK_MESSAGE);
  const showRetry = !isConfigError;

  const handleRetryGoogle = async () => {
    // Build return_to from current path + search, excluding auth_error
    const url = new URL(window.location.href);
    url.searchParams.delete('auth_error');
    let returnTo = url.pathname + (url.search || '');
    if (!returnTo || !returnTo.startsWith('/') || returnTo.includes('://') || returnTo.startsWith('//')) {
      returnTo = '/app';
    }
    if (isNative()) {
      const oauthUrl = `${BACKEND_URL}/api/auth/google/oauth/start?return_to=${encodeURIComponent(returnTo)}&client=mobile`;
      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: oauthUrl });
      } catch (e) {
        // Fallback: nothing to do — user sees the banner still
      }
      return;
    }
    window.location.assign(`/api/auth/google/oauth/start?return_to=${encodeURIComponent(returnTo)}`);
  };

  const handleDismiss = () => {
    setAuthError(null);
  };

  const bannerClass = variant === 'login' ? 'auth-error-login' : 'auth-error-app';

  return (
    <div className={`auth-error-banner ${bannerClass}`} data-testid="auth-error-banner">
      <div className="auth-error-icon">
        <AlertCircle size={18} />
      </div>
      <div className="auth-error-body">
        {isConfigError ? (
          <NotConfiguredContent />
        ) : (
          <>
            <p className="auth-error-message">{message}</p>
            {showRetry && (
              <button
                className="auth-error-retry"
                onClick={handleRetryGoogle}
                data-testid="auth-error-retry-btn"
              >
                <RefreshCw size={14} />
                Try Google again
              </button>
            )}
          </>
        )}
      </div>
      <button className="auth-error-dismiss" onClick={handleDismiss} aria-label="Dismiss">
        <X size={16} />
      </button>
    </div>
  );
}

export default AuthErrorBanner;
